import type { HistoryListItemProps } from './HistoryListItem';

import { useCallback, useEffect, useRef } from 'react';

import { RefreshControl, StyleSheet } from 'react-native';

import { useFocusEffect } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { getColorTokenValue } from '@utility-nyc/react-native-ui-config';
import { FlatList } from 'react-native-gesture-handler';
import { useMedia, YStack } from 'tamagui';

import { Toast } from '@shared/utils';
import { BodyL } from '@south-street-app/atoms';
import { ScreenWrapper } from '@south-street-app/molecules';
import {
  useGetAnalyticFirmAssociates,
  useGetEndUserTradeHistory,
} from '@utility-nyc/react-query-sdk';

import { HistoryListEmptyComponent } from './HistoryListEmptyComponent';
import { HistoryListItem } from './HistoryListItem';
import { HistoryListLoadingComponent } from './HistoryListLoadingComponent';

const SENTRY_TAG_TYPE = 'screen';
const SENTRY_TAG_CONTEXT = 'HistoryScreen';
const HISTORY_ERROR_TITLE = 'Could not load history';
const HISTORY_ERROR_SUBTITLE = 'Please try again.';

const styles = StyleSheet.create({
  desktopContainer: {
    width: 800,
    alignSelf: 'center',
  },
  sideBorders: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: getColorTokenValue('$mono300'),
  },
});

const HistoryListErrorComponent = () => (
  <YStack
    flex={1}
    justifyContent={'center'}
    alignItems={'center'}
    paddingTop={'$15'}
    paddingHorizontal={'$12'}
    space={'$2'}
  >
    <BodyL textAlign={'center'}>{HISTORY_ERROR_TITLE}</BodyL>
    <BodyL textAlign={'center'}>{HISTORY_ERROR_SUBTITLE}</BodyL>
  </YStack>
);

const HistoryScreen = () => {
  const { desktop } = useMedia();
  const { data, error, isLoading, isRefetching, refetch } =
    useGetEndUserTradeHistory();
  const { error: associatesError } = useGetAnalyticFirmAssociates();
  const lastTradeHistoryErrorRef = useRef<unknown>(undefined);
  const lastAssociatesErrorRef = useRef<unknown>(undefined);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  useEffect(() => {
    if (!error || lastTradeHistoryErrorRef.current === error) {
      return;
    }

    lastTradeHistoryErrorRef.current = error;

    Sentry.captureException(error, {
      tags: {
        type: SENTRY_TAG_TYPE,
        context: SENTRY_TAG_CONTEXT,
        method: 'useGetEndUserTradeHistory',
      },
    });

    Toast.showErrorToast({
      text1: HISTORY_ERROR_TITLE,
      text2: HISTORY_ERROR_SUBTITLE,
    });
  }, [error]);

  useEffect(() => {
    if (
      !associatesError ||
      lastAssociatesErrorRef.current === associatesError
    ) {
      return;
    }

    lastAssociatesErrorRef.current = associatesError;

    Sentry.captureException(associatesError, {
      tags: {
        type: SENTRY_TAG_TYPE,
        context: SENTRY_TAG_CONTEXT,
        method: 'useGetAnalyticFirmAssociates',
      },
      extra: {
        blocking: false,
      },
    });
  }, [associatesError]);

  const renderItem = useCallback(
    ({ item }: HistoryListItemProps) => <HistoryListItem item={item} />,
    [],
  );

  const EmptyComponent = useCallback(() => <HistoryListEmptyComponent />, []);
  const ErrorComponent = useCallback(() => <HistoryListErrorComponent />, []);

  const LoadingComponent = useCallback(
    () => <HistoryListLoadingComponent />,
    [],
  );

  const onRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <ScreenWrapper>
      <FlatList
        data={isLoading || error ? [] : data?.data.tradeRequests}
        renderItem={renderItem}
        ListEmptyComponent={
          isLoading ? LoadingComponent : error ? ErrorComponent : EmptyComponent
        }
        contentContainerStyle={[
          desktop ? styles.desktopContainer : undefined,
          !isLoading && !error && data?.data.tradeRequests
            ? styles.sideBorders
            : undefined,
        ]}
        refreshControl={
          <RefreshControl refreshing={isRefetching} onRefresh={onRefresh} />
        }
      />
    </ScreenWrapper>
  );
};

export { HistoryScreen };
